import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { usePermissions } from '../auth/usePermissions';
import { listShipTos, type ShipToDto } from './api';
import { ShipToFormModal } from './ShipToFormModal';

type Scope = 'active' | 'all';

/**
 * The ship-to book — the delivery addresses a purchase order can be sent to.
 *
 * This table has no equivalent in the current product, which retypes the address on every order.
 * The result there is that one warehouse ends up with three spellings of itself and the supplier
 * ships to the wrong door. Here it is a short list somebody maintains once.
 *
 * An address ON an order cannot be deleted, only deactivated: the order keeps a copy of what was
 * typed at the time, and the entry is what tells you those copies came from the same place.
 */
export function ShipTosRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<ShipToDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');

  const canWrite = permissions.has('po:write');

  const shipTos = useQuery({
    queryKey: ['ship-tos', scope] as const,
    queryFn: ({ signal }) => listShipTos(scope === 'all', signal),
  });

  const rows = useMemo(() => shipTos.data ?? [], [shipTos.data]);

  function onSaved() {
    void queryClient.invalidateQueries({ queryKey: ['ship-tos'] });
  }

  /**
   * The name is what people pick from ("Main dock", "Back gate"); the street line under it is how
   * they tell two docks apart. Both badges ride in the aside, because "inactive" and "on an order"
   * are the two things that decide whether a row can be touched.
   */
  const columns: DataColumn<ShipToDto>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('shipTos.name'),
        card: 'title',
        render: (s) => (
          <Text size="sm" fw={500}>
            {s.shipToName}
          </Text>
        ),
      },
      {
        key: 'address',
        label: t('shipTos.address'),
        card: 'subtitle',
        render: (s) => (
          <>
            <Text size="sm">{s.addressLine1 ?? '—'}</Text>
            <Text size="xs" c="dimmed">
              {[s.city, s.stateProvince, s.postalCode].filter(Boolean).join(', ')}
            </Text>
          </>
        ),
      },
      { key: 'phone', label: t('shipTos.phone'), render: (s) => s.phone ?? '—' },
      {
        key: 'state',
        label: t('shipTos.state'),
        card: 'aside',
        render: (s) => (
          <Group gap={6}>
            {!s.isActive && (
              <Badge color="gray" variant="light">
                {t('shipTos.inactive')}
              </Badge>
            )}
            {s.isInUse && (
              <Tooltip label={t('shipTos.inUseHint')}>
                <Badge color="blue" variant="light">
                  {t('shipTos.inUse')}
                </Badge>
              </Tooltip>
            )}
          </Group>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('nav.shipTos')}
        actions={
          canWrite && (
            <Button
              color="invetrak"
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditing(null);
                open();
              }}
            >
              {t('shipTos.new')}
            </Button>
          )
        }
      />

      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('shipTos.intro')}
        </Text>

        <SegmentedControl
          value={scope}
          onChange={(v) => setScope(v as Scope)}
          data={[
            { value: 'active', label: t('shipTos.scopeActive') },
            { value: 'all', label: t('shipTos.scopeAll') },
          ]}
          w={{ base: '100%', sm: 260 }}
        />

        {shipTos.isLoading ? (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        ) : shipTos.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('shipTos.errors.listFailed')}
          </Alert>
        ) : rows.length === 0 ? (
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('shipTos.none')}
          </Alert>
        ) : (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(s) => s.purchaseOrderShipToId}
            minWidth={700}
            actionsWidth={60}
            striped
            highlightOnHover
            actions={
              canWrite
                ? (s) => (
                    <ActionIcon
                      variant="subtle"
                      aria-label={t('shipTos.editAria', { name: s.shipToName })}
                      onClick={() => {
                        setEditing(s);
                        open();
                      }}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  )
                : undefined
            }
          />
        )}
      </Stack>

      <ShipToFormModal opened={modalOpen} onClose={close} shipTo={editing} onSaved={onSaved} />
    </>
  );
}
